import { Link } from "react-router-dom";
import styled from "styled-components";
import { CardComp, ButtonComp } from "..";
import { formatMoney } from "../../utils/currencyFormat";
import { convertToTitleCase } from "../../utils/helpers";
import InvestorStatusBadge from "./InvestorStatusBadge";
import moment from "moment";

const TYPE_LABELS = {
  investment: "Investment",
  marketplace: "Marketplace order",
  cooperative: "Cooperative request",
  real_estate: "Property request",
};

const RequestCard = styled(CardComp)`
  && {
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  &:hover {
    border-color: var(--color-primary-soft) !important;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06) !important;
  }
`;

function getRequestDetailPath(req) {
  if (req?.type === "marketplace") {
    const orderNumber = req.orderNumber || req.reference || req.request_reference;
    if (orderNumber) return `/invest/marketplace/orders/${encodeURIComponent(orderNumber)}`;
  }
  const key = req?.reference || req?.request_reference;
  if (key) return `/invest/requests/${encodeURIComponent(key)}`;
  if (req?.id) return `/invest/requests/${req.id}`;
  return null;
}

function getRequestAmount(req) {
  const payload = req?.payload || {};
  if (payload.totalNgn != null) return formatMoney(payload.totalNgn);
  if (payload.estimatedValueNgn != null) return formatMoney(payload.estimatedValueNgn);
  return null;
}

export default function InvestorRequestsList({
  requests = [],
  emptyMessage = "No requests yet.",
  showViewDetails = true,
  linkToRequests = false,
}) {
  if (!requests.length) {
    return (
      <CardComp cardClassName="border-0 shadow-sm rounded-4 text-center py-5 investor-empty-state" data-tour="invest-requests-list">
        <p className="text-muted mb-0 px-3">{emptyMessage}</p>
        {linkToRequests ? (
          <Link to="/invest/requests" className="d-inline-block mt-3 small fw-semibold" style={{ color: "var(--color-secondary)" }}>
            Go to My Requests →
          </Link>
        ) : null}
      </CardComp>
    );
  }

  return (
    <div className="d-flex flex-column gap-3" data-tour="invest-requests-list">
      {requests.map((req) => {
        const detailPath = getRequestDetailPath(req);
        const amount = getRequestAmount(req);
        const phone = req.payload?.phone;

        return (
          <RequestCard key={req.id} cardClassName="border-0 shadow-sm rounded-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-3">
              <div className="min-w-0 flex-grow-1">
                <InvestorStatusBadge
                  status={req.type}
                  label={TYPE_LABELS[req.type] || convertToTitleCase(req.type)}
                  backgroundColor="var(--color-secondary)"
                  className="mb-2"
                />
                <h6 className="fw-bold mb-1">{req.payload?.title || req.payload?.name || "Request"}</h6>
                <p className="text-muted small mb-0">
                  {moment(req.created_at).format("DD-MMM-YYYY HH:mm")}
                </p>
                {req.payload?.quantity != null ? (
                  <p className="text-muted small mb-0 mt-1">
                    Qty {req.payload.quantity}
                    {req.payload.unit ? ` · ${req.payload.unit}` : ""}
                  </p>
                ) : req.payload?.units != null ? (
                  <p className="text-muted small mb-0 mt-1">
                    {req.payload.units} unit{req.payload.units === 1 ? "" : "s"}
                    {req.payload.contact_method ? ` · Contact via ${req.payload.contact_method}` : ""}
                  </p>
                ) : null}
                {phone ? (
                  <p className="text-muted small mb-0 mt-1">Phone: {phone}</p>
                ) : null}
              </div>
              <div className="text-md-end flex-shrink-0 d-flex flex-column align-items-md-end gap-2">
                {amount ? (
                  <p className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>
                    {amount}
                  </p>
                ) : null}
                <InvestorStatusBadge status={req.status || "pending"} />
                {showViewDetails && detailPath ? (
                  <Link to={detailPath} className="mt-1">
                    <ButtonComp
                      btnText="View details"
                      btnClassName="btn btn-sm px-3 py-2 rounded-3 border"
                      ButtonInlineStyle={{
                        minHeight: 36,
                        fontSize: "0.8rem",
                        borderColor: "var(--color-primary)",
                        color: "var(--color-primary)",
                        background: "#fff",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </Link>
                ) : null}
              </div>
            </div>
          </RequestCard>
        );
      })}
    </div>
  );
}
